import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { motion } from "framer-motion";
import { usePlayerAuth } from "../context/PlayerAuthContext";
import { ArrowLeft, Calendar, Users, Clapperboard } from "lucide-react";
import { API, GOLD, fmtDay, ClipThumb } from "../components/recaps/recapKit";

const RecapCard = ({ r, token, i, onOpen }) => (
  <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4, delay: Math.min(i, 8) * 0.06 }}
    className="rounded-xl border border-white/10 border-l-4 border-l-[#D4AF37] bg-[#1A1A1A] overflow-hidden hover:border-white/25 hover:border-l-[#D4AF37] transition-colors">
    {r.clip ? <ClipThumb clip={r.clip} token={token} testid={`recap-card-thumb-${r.id}`} onClick={onOpen} className="w-full aspect-[9/16] rounded-none border-0">
      {r.clip.move_label && <span className="absolute left-2 bottom-2 px-2 py-1 rounded-sm bg-black/70 backdrop-blur-sm font-mono text-[10px] uppercase tracking-wider" style={{ color: GOLD }}>{r.clip.move_label}</span>}
    </ClipThumb> : <button onClick={onOpen} className="w-full aspect-[9/16] flex items-center justify-center bg-[#0a0a0a] text-white/30"><Clapperboard size={26} /></button>}
    <button data-testid={`recap-card-${r.id}`} onClick={onOpen} className="w-full text-left p-4 min-h-[44px]">
      <div className="font-headings text-base sm:text-lg uppercase tracking-wide truncate">{r.name}</div>
      <div className="flex flex-wrap gap-x-3 gap-y-1 mt-1 font-mono text-[10px] uppercase tracking-wider text-white/45">
        <span className="inline-flex items-center gap-1"><Calendar size={11} /> {fmtDay(r.date)}</span>
        <span data-testid={`recap-card-players-${r.id}`} className="inline-flex items-center gap-1"><Users size={11} /> {r.player_count} player{r.player_count === 1 ? "" : "s"}</span>
      </div>
    </button>
  </motion.div>
);

export default function Recaps() {
  const navigate = useNavigate();
  const { token } = usePlayerAuth();
  const [rows, setRows] = useState(null);
  useEffect(() => { axios.get(`${API}/me/recaps`, { headers: { Authorization: `Bearer ${token}` } }).then(({ data }) => setRows(data.recaps)).catch(() => setRows([])); }, [token]);
  return (
    <div className="min-h-screen bg-[#111111] text-white grain" data-testid="recaps-page">
      <main className="max-w-5xl mx-auto px-5 sm:px-8 py-6 sm:py-10 pb-24">
        <button data-testid="recaps-back" onClick={() => navigate(-1)} className="inline-flex items-center gap-1.5 min-h-[44px] text-xs font-bold uppercase tracking-wider text-white/60 hover:text-white transition-colors"><ArrowLeft size={14} /> Back</button>
        <div className="font-mono text-[10px] font-bold uppercase tracking-[0.35em] mt-4" style={{ color: GOLD }}>Your training, on film</div>
        <h1 className="font-cover text-5xl sm:text-6xl uppercase leading-[0.9] mt-2 mb-8">Session Recaps</h1>
        {rows === null ? <div className="text-white/40 text-sm">Loading…</div> : rows.length === 0 ? (
          <p data-testid="recaps-empty" className="rounded-2xl border border-dashed border-white/15 p-8 text-white/55">Your first session recap will appear here after your next training session with Coach Barker.</p>
        ) : (
          <div data-testid="recaps-list" className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3 sm:gap-4">
            {rows.map((r, i) => <RecapCard key={r.id} r={r} i={i} token={token} onOpen={() => navigate(`/recaps/${r.id}`)} />)}
          </div>
        )}
      </main>
    </div>
  );
}
